<template>
    <div>
        <c-gallery
                v-if="items.length"
                :images="galleryImages"
                :index="openIndex"
                :options="galleryOptions"
                :id="viewerId"
                :key="viewerKey"
                ref="gallery"
                @close="handleGalleryClose"
                @onslide="handleGallerySlide"
                @onclosed="handleGalleryClosed"
        />

        <div
                v-if="showToolbar"
                class="app-file-viewer-toolbar"
        >
            <a
                    :href="currentFile.downloadUrl"
                    :download="currentFile.name"
                    target="_blank"
                    rel="noopener"
                    class="app-file-viewer-tool"
                    title="Скачать"
                    @click.stop
            >
                <i class="fas fa-download"></i>
            </a>
            <button
                    v-if="currentFile.isImage"
                    type="button"
                    class="app-file-viewer-tool"
                    :disabled="!canZoomOut"
                    title="Уменьшить"
                    @click.stop="zoomOut"
            >
                <i class="fas fa-search-minus"></i>
            </button>
            <button
                    v-if="currentFile.isImage"
                    type="button"
                    class="app-file-viewer-tool"
                    :disabled="!canZoomIn"
                    title="Увеличить"
                    @click.stop="zoomIn"
            >
                <i class="fas fa-search-plus"></i>
            </button>
            <button
                    v-if="currentFile.isImage"
                    type="button"
                    class="app-file-viewer-tool app-file-viewer-tool--wide"
                    :disabled="zoomPercent === 100"
                    title="Сбросить масштаб"
                    @click.stop="resetZoom"
            >
                {{ zoomPercent }}%
            </button>
        </div>

        <div
                v-if="isOpen && currentFile && currentFile.reportAnnulled"
                class="app-file-viewer-annulled"
                role="alert"
        >
            <strong><i class="fas fa-ban"></i> ОТЧЁТ АННУЛИРОВАН — НЕ УЧИТЫВАТЬ</strong>
            <span v-if="currentFile.reportAnnulmentReason">
                Причина: {{ currentFile.reportAnnulmentReason }}
            </span>
        </div>

        <div
                v-if="showVideoPanel"
                class="app-file-viewer-video-panel"
                @click.stop
        >
            <video
                    :key="currentFile.url"
                    :src="currentFile.url"
                    controls
                    autoplay
                    playsinline
                    preload="metadata"
            ></video>
        </div>

        <div
                v-if="showFileActions"
                class="app-file-viewer-file-actions"
        >
            <a
                    :href="currentFile.url"
                    target="_blank"
                    rel="noopener"
                    class="app-file-viewer-open-cta"
                    @click.stop
            >
                <i class="fas fa-external-link-alt"></i>
                <span>Открыть</span>
            </a>
            <a
                    :href="currentFile.downloadUrl"
                    :download="currentFile.name"
                    target="_blank"
                    rel="noopener"
                    class="app-file-viewer-download-cta"
                    @click.stop
            >
                <span class="app-file-viewer-download-cta__icon">
                    <i :class="currentFile.iconClass"></i>
                </span>
                <span class="app-file-viewer-download-cta__text">
                    <strong>Скачать файл</strong>
                    <small>{{ currentFile.name }}</small>
                </span>
            </a>
        </div>

        <div
                v-if="isOpen && currentNotice"
                class="app-file-viewer-notice"
        >
            <div class="app-file-viewer-notice-card">
                <strong>{{ currentNotice.title }}</strong>
                <p>{{ currentNotice.body }}</p>
                <div class="app-file-viewer-notice-actions">
                    <span>
                        <i class="fas fa-arrow-left"></i>
                        {{ currentNotice.leftText }}
                    </span>
                    <span>
                        {{ currentNotice.rightText }}
                        <i class="fas fa-arrow-right"></i>
                    </span>
                </div>
            </div>
        </div>

        <button
                v-if="activePrimaryAction"
                type="button"
                class="app-file-viewer-primary-action"
                :disabled="primaryActionDisabled || primaryActionInProgress"
                @click.stop="runPrimaryAction"
        >
            <b-spinner v-if="primaryActionInProgress" small class="mr-1"></b-spinner>
            <i v-else-if="activePrimaryAction.icon" :class="`${activePrimaryAction.icon} mr-1`"></i>
            {{ activePrimaryAction.label }}
        </button>
    </div>
</template>

<script>
import VueGallery from 'vue-gallery';
import 'blueimp-gallery/js/blueimp-gallery-indicator.js';
import fileViewerService from './fileViewerService';
import {
    normalizeFileViewerFiles,
    resolveFileViewerStartIndex
} from '@/utils/fileViewer';

const VIEWER_ID = `app-file-viewer`;
const VIEWER_KEY_PREFIX = `app_file_viewer`;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;
const WHEEL_ZOOM_STEP = 0.25;
const DOUBLE_TAP_ZOOM = 2;
const DOUBLE_TAP_DELAY_MS = 300;
const TAP_MAX_DURATION_MS = 250;
const TAP_MOVE_TOLERANCE_PX = 12;
const DOUBLE_TAP_DISTANCE_PX = 32;
const CLOSE_CLEANUP_DELAY_MS = 450;
const ZOOM_APPLY_DELAY_MS = 80;
const THUMBNAIL_SYNC_DELAY_MS = 90;
const DEFAULT_NOTICE_DIRECTION = `forward`;
const SWIPE_CLOSE_DIRECTION_LOCK_PX = 12;
const SWIPE_CLOSE_MIN_DISTANCE_PX = 72;
const SWIPE_CLOSE_VIEWPORT_RATIO = 0.12;
const SWIPE_CLOSE_MAX_DISTANCE_PX = 120;
const SWIPE_CLOSE_VERTICAL_RATIO = 1.15;

export default {
    name: `AppFileViewer`,
    components: {
        cGallery: VueGallery
    },
    data() {
        return {
            viewerKey: `${VIEWER_KEY_PREFIX}_${Date.now()}`,
            items: [],
            openIndex: null,
            currentIndex: null,
            primaryAction: null,
            primaryActionInProgress: false,
            zoom: MIN_ZOOM,
            tapCandidate: null,
            lastImageTap: null,
            panX: 0,
            panY: 0,
            isPanning: false,
            panStartX: 0,
            panStartY: 0,
            panOriginX: 0,
            panOriginY: 0,
            isPinching: false,
            pinchStartDistance: 0,
            pinchStartZoom: 1,
            isSwipeCloseTracking: false,
            isSwipeCloseActive: false,
            swipeCloseStartX: 0,
            swipeCloseStartY: 0,
            swipeCloseOffsetY: 0,
            swipeCloseContent: null,
            currentSlideElement: null,
            historyPushed: false,
            closingFromPopstate: false,
            restoringHistory: false,
            openToken: 0,
            isClosing: false,
            bodyOverflowBeforeOpen: null,
            closeCleanupTimer: null,
            continuousNavigation: true,
            navigationDirection: null
        };
    },
    computed: {
        viewerId() {
            return VIEWER_ID;
        },
        isOpen() {
            return this.openIndex !== null && this.openIndex !== undefined;
        },
        activeIndex() {
            return this.currentIndex !== null && this.currentIndex !== undefined
                ? this.currentIndex
                : this.openIndex;
        },
        currentFile() {
            if (!this.isOpen) {
                return null;
            }
            return this.items[this.activeIndex] || null;
        },
        showToolbar() {
            return Boolean(this.isOpen && this.currentFile && !this.currentFile.isViewerNotice);
        },
        showVideoPanel() {
            return Boolean(this.isOpen && this.currentFile && this.currentFile.isVideo);
        },
        showFileActions() {
            return Boolean(
                this.isOpen
                && this.currentFile
                && !this.currentFile.isImage
                && !this.currentFile.isVideo
                && !this.currentFile.isViewerNotice
            );
        },
        canZoomCurrentFile() {
            return Boolean(
                this.isOpen
                && this.currentFile
                && this.currentFile.isImage
                && !this.currentFile.isViewerNotice
            );
        },
        galleryImages() {
            return this.items.map(this.toGalleryImage);
        },
        galleryOptions() {
            return {
                closeOnSlideClick: false,
                closeOnSwipeUpOrDown: false,
                toggleControlsOnSlideClick: false,
                stretchImages: false,
                thumbnailIndicators: true,
                thumbnailProperty: `thumbnail`,
                preloadRange: 1,
                continuous: this.continuousNavigation
            };
        },
        currentNotice() {
            if (!this.currentFile || !this.currentFile.isViewerNotice) {
                return null;
            }

            const notice = this.currentFile.viewerNotice || {};
            const direction = this.navigationDirection || notice.defaultDirection || DEFAULT_NOTICE_DIRECTION;
            const data = notice[direction] || notice.forward || notice.backward || notice;
            return {
                title: data.title || this.currentFile.name || `Просмотр смены`,
                body: data.body || ``,
                leftText: data.leftText || `Влево`,
                rightText: data.rightText || `Вправо`
            };
        },
        boundedZoom() {
            const zoom = Number(this.zoom) || MIN_ZOOM;
            return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
        },
        zoomPercent() {
            return Math.round(this.boundedZoom * 100);
        },
        canZoomIn() {
            return this.boundedZoom < MAX_ZOOM;
        },
        canZoomOut() {
            return this.boundedZoom > MIN_ZOOM;
        },
        activePrimaryAction() {
            if (!this.currentFile || this.currentFile.isViewerNotice || !this.primaryAction) {
                return null;
            }
            if (
                typeof this.primaryAction.visible === `function`
                && !this.primaryAction.visible(this.currentFile, this)
            ) {
                return null;
            }
            return this.primaryAction;
        },
        primaryActionDisabled() {
            if (!this.activePrimaryAction) {
                return true;
            }
            if (typeof this.activePrimaryAction.disabled === `function`) {
                return Boolean(this.activePrimaryAction.disabled(this.currentFile, this));
            }
            return Boolean(this.activePrimaryAction.disabled);
        }
    },
    watch: {
        isOpen(value) {
            if (value) {
                this.addWheelListener();
                this.setViewerActiveClass(true);
            } else {
                this.removeWheelListener();
                this.setViewerActiveClass(false);
            }
        },
        '$route.fullPath'() {
            if (this.isOpen) {
                this.close({restoreHistory: false});
            }
        }
    },
    mounted() {
        fileViewerService.register(this);
        if (typeof window !== `undefined`) {
            window.addEventListener(`popstate`, this.handlePopState);
        }
    },
    beforeDestroy() {
        fileViewerService.unregister(this);
        if (typeof window !== `undefined`) {
            window.removeEventListener(`popstate`, this.handlePopState);
        }
        this.clearCloseCleanupTimer();
        this.resetDoubleTap();
        this.resetSwipeClose();
        this.removePanListeners();
        this.removePinchListeners();
        this.removeWheelListener();
        this.restorePageScroll();
    },
    methods: {
        createViewerKey() {
            return `${VIEWER_KEY_PREFIX}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        },
        toGalleryImage(file) {
            const imageUrl = file.isImage ? file.url : file.placeholderUrl;
            return {
                href: file.isViewerNotice ? file.placeholderUrl : imageUrl,
                thumbnail: file.isViewerNotice
                    ? (file.thumbnailUrl || file.placeholderUrl)
                    : (file.isImage ? (file.thumbnailUrl || file.url) : file.placeholderUrl),
                title: file.name
            };
        },
        setViewerActiveClass(value) {
            if (typeof document === `undefined` || !document.body) {
                return;
            }
            if (value) {
                document.body.classList.add(`app-file-viewer-active`);
            } else {
                document.body.classList.remove(`app-file-viewer-active`);
            }
        },
        getViewerElement() {
            if (typeof document === `undefined`) {
                return null;
            }
            return document.getElementById(this.viewerId);
        },
        resetCurrentState() {
            this.openIndex = null;
            this.currentIndex = null;
            this.currentSlideElement = null;
            this.primaryAction = null;
            this.primaryActionInProgress = false;
            this.navigationDirection = null;
        },
        resetInteractionState() {
            this.resetSwipeClose();
            this.resetZoom();
            this.removeWheelListener();
            this.removePanListeners();
            this.removePinchListeners();
        },
        prepareOpenState(normalized, options, openToken) {
            this.clearCloseCleanupTimer();
            this.openToken = openToken;
            this.isClosing = false;
            this.bodyOverflowBeforeOpen = this.getBodyOverflow();
            this.viewerKey = this.createViewerKey();
            this.items = normalized;
            this.primaryAction = options.primaryAction || null;
            this.continuousNavigation = options.continuous === false ? false : true;
            this.openIndex = null;
            this.currentIndex = null;
            this.currentSlideElement = null;
            this.navigationDirection = null;
            this.primaryActionInProgress = false;
            this.resetZoom();
        },
        open(files, options = {}) {
            const normalized = normalizeFileViewerFiles(files);
            if (!normalized.length) {
                return;
            }

            const startIndex = resolveFileViewerStartIndex(normalized, options);
            if (this.shouldOpenExternally(normalized[startIndex])) {
                this.openExternalFile(normalized[startIndex]);
                return;
            }
            const openToken = this.openToken + 1;
            this.prepareOpenState(normalized, options, openToken);
            this.pushHistoryState(options);
            this.$nextTick(() => {
                if (this.openToken !== openToken) {
                    return;
                }
                this.openIndex = startIndex;
                this.currentIndex = startIndex;
                this.scheduleZoomApply();
                this.scheduleThumbnailSync(startIndex);
            });
        },
        close(options = {}) {
            this.openToken += 1;
            if (!this.items.length && !this.isOpen && !this.isClosing) {
                this.restorePageScroll();
                return;
            }
            const shouldRestoreHistory = options.restoreHistory !== false
                && this.historyPushed
                && !this.closingFromPopstate
                && typeof window !== `undefined`
                && window.history
                && typeof window.history.back === `function`;

            this.isClosing = true;
            this.resetCurrentState();
            this.resetInteractionState();
            this.historyPushed = false;
            this.scheduleCloseCleanup();

            if (shouldRestoreHistory) {
                this.restoringHistory = true;
                window.__appFileViewerSuppressHistoryBack = true;
                window.__mechanicAssetCardSuppressTelegramBack = true;
                window.history.back();
            }
            this.closingFromPopstate = false;
        },
        finishClose() {
            this.clearCloseCleanupTimer();
            this.resetCurrentState();
            this.items = [];
            this.isClosing = false;
            this.resetInteractionState();
            this.restorePageScroll();
        },
        scheduleCloseCleanup() {
            this.clearCloseCleanupTimer();
            if (typeof window === `undefined`) {
                this.finishClose();
                return;
            }
            this.closeCleanupTimer = window.setTimeout(() => {
                this.finishClose();
            }, CLOSE_CLEANUP_DELAY_MS);
        },
        clearCloseCleanupTimer() {
            if (this.closeCleanupTimer && typeof window !== `undefined`) {
                window.clearTimeout(this.closeCleanupTimer);
            }
            this.closeCleanupTimer = null;
        },
        getBodyOverflow() {
            if (typeof document === `undefined` || !document.body) {
                return null;
            }
            return document.body.style.overflow || ``;
        },
        restorePageScroll() {
            if (typeof document === `undefined` || !document.body) {
                return;
            }
            this.setViewerActiveClass(false);
            if (document.body.style.overflow === `hidden`) {
                document.body.style.overflow = this.bodyOverflowBeforeOpen || ``;
            }
            this.bodyOverflowBeforeOpen = null;
        },
        shouldOpenExternally(file) {
            return Boolean(file && !file.isImage && !file.isVideo && !file.isViewerNotice);
        },
        openExternalFile(file) {
            if (!file || typeof window === `undefined`) {
                return;
            }
            const url = file.downloadUrl || file.url;
            if (!url || url === `#`) {
                return;
            }
            window.open(url, `_blank`, `noopener`);
        },
        pushHistoryState(options = {}) {
            if (
                options.pushHistory === false
                || this.historyPushed
                || typeof window === `undefined`
                || !window.history
                || !window.history.pushState
            ) {
                return;
            }
            window.history.pushState({
                appFileViewer: true,
                source: options.source || null
            }, document.title, window.location.href);
            this.historyPushed = true;
        },
        handlePopState() {
            if (this.restoringHistory) {
                this.restoringHistory = false;
                return;
            }
            if (!this.isOpen && !this.historyPushed) {
                return;
            }
            this.closingFromPopstate = true;
            this.close({restoreHistory: false});
        },
        handleGalleryClose() {
            if (this.isClosing) {
                return;
            }
            this.close();
        },
        handleGalleryClosed() {
            this.finishClose();
        },
        getNavigationDirection(nextIndex) {
            const previousIndex = Number.isInteger(this.currentIndex)
                ? this.currentIndex
                : this.openIndex;
            if (!Number.isInteger(previousIndex) || nextIndex === previousIndex) {
                return null;
            }
            return nextIndex > previousIndex ? `forward` : `backward`;
        },
        handleGallerySlide(payload) {
            const index = payload && payload.index;
            if (Number.isInteger(index)) {
                this.resetSwipeClose();
                this.navigationDirection = this.getNavigationDirection(index);
                this.currentIndex = index;
                this.currentSlideElement = payload ? payload.slide : null;
                this.resetZoom();
                this.scheduleZoomApply();
                this.scheduleThumbnailSync(index);
            }
        },
        scheduleThumbnailSync(fallbackIndex) {
            const token = this.openToken;
            const sync = () => {
                if (this.openToken !== token || !this.isOpen) {
                    return;
                }
                const index = Number.isInteger(this.currentIndex)
                    ? this.currentIndex
                    : fallbackIndex;
                this.scrollActiveThumbnailIntoView(index);
            };

            this.$nextTick(() => {
                sync();
                if (typeof window === `undefined`) {
                    return;
                }

                this.requestNextFrame(sync);
                window.setTimeout(sync, THUMBNAIL_SYNC_DELAY_MS);
            });
        },
        scrollActiveThumbnailIntoView(index) {
            const container = this.getViewerElement();
            const indicator = container ? container.querySelector(`.indicator`) : null;
            if (!indicator) {
                return;
            }

            const thumbnails = Array.from(indicator.children || [])
                .filter(element => element && element.nodeType === 1);
            const indexedThumbnail = Number.isInteger(index) ? thumbnails[index] : null;
            const activeThumbnail = indicator.querySelector(`.active`);
            const thumbnail = indexedThumbnail || activeThumbnail;
            if (!thumbnail || !indicator.contains(thumbnail)) {
                return;
            }

            const viewportWidth = Number(indicator.clientWidth) || 0;
            const scrollWidth = Number(indicator.scrollWidth) || 0;
            const maxScrollLeft = Math.max(0, scrollWidth - viewportWidth);
            if (!viewportWidth || !maxScrollLeft) {
                return;
            }

            const thumbnailLeft = Number(thumbnail.offsetLeft) || 0;
            const thumbnailWidth = Number(thumbnail.offsetWidth) || 0;
            const centeredLeft = thumbnailLeft - ((viewportWidth - thumbnailWidth) / 2);
            const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, centeredLeft));
            if (Math.abs((Number(indicator.scrollLeft) || 0) - nextScrollLeft) < 1) {
                return;
            }

            indicator.scrollLeft = nextScrollLeft;
        },
        requestNextFrame(callback) {
            if (typeof window === `undefined`) {
                return;
            }
            if (typeof window.requestAnimationFrame === `function`) {
                window.requestAnimationFrame(callback);
            } else {
                window.setTimeout(callback, 0);
            }
        },
        preventViewerEvent(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (event && event.stopPropagation) {
                event.stopPropagation();
            }
        },
        normalizeZoom(value) {
            const zoom = Number(value) || MIN_ZOOM;
            return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(zoom * 100) / 100));
        },
        setZoom(value, options = {}) {
            this.resetSwipeClose();
            this.zoom = this.normalizeZoom(value);
            if (this.zoom === MIN_ZOOM) {
                this.resetPan();
            }
            if (options.immediate === true) {
                this.applyZoom();
            } else {
                this.scheduleZoomApply();
            }
        },
        zoomIn() {
            this.setZoom(this.boundedZoom + ZOOM_STEP);
        },
        zoomOut() {
            this.setZoom(this.boundedZoom - ZOOM_STEP);
        },
        resetZoom() {
            this.resetDoubleTap();
            this.resetPinch();
            this.resetPan();
            this.setZoom(MIN_ZOOM);
        },
        handleWheel(event) {
            if (!this.canZoomCurrentFile) {
                return;
            }
            this.preventViewerEvent(event);
            const delta = Number(event && event.deltaY) || 0;
            if (delta === 0) {
                return;
            }
            const step = delta < 0 ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP;
            this.setZoom(this.boundedZoom + step);
        },
        resetPan() {
            this.panX = 0;
            this.panY = 0;
            this.isPanning = false;
            this.panStartX = 0;
            this.panStartY = 0;
            this.panOriginX = 0;
            this.panOriginY = 0;
            this.removePanListeners();
        },
        resetPinch() {
            this.isPinching = false;
            this.pinchStartDistance = 0;
            this.pinchStartZoom = 1;
            this.removePinchListeners();
        },
        scheduleZoomApply() {
            this.$nextTick(() => {
                this.applyZoom();
                if (typeof window !== `undefined`) {
                    window.setTimeout(() => this.applyZoom(), ZOOM_APPLY_DELAY_MS);
                }
            });
        },
        alignToDevicePixel(value) {
            const number = Number(value) || 0;
            if (!Number.isFinite(number)) {
                return 0;
            }
            const ratio = typeof window !== `undefined`
                ? Number(window.devicePixelRatio) || 1
                : 1;
            return Math.round(number * ratio) / ratio;
        },
        applyZoom() {
            const container = this.getViewerElement();
            if (!container) {
                return;
            }
            const contents = Array.from(container.querySelectorAll(`.slides > .slide > .slide-content`));
            const index = this.activeIndex;
            const activeSlide = this.currentSlideElement && container.contains(this.currentSlideElement)
                ? this.currentSlideElement
                : null;
            const activeContent = activeSlide ? activeSlide.querySelector(`.slide-content`) : null;
            contents.forEach((content, contentIndex) => {
                const active = activeContent ? content === activeContent : contentIndex === index;
                const canZoom = active && this.canZoomCurrentFile;
                const zoom = canZoom ? this.boundedZoom : MIN_ZOOM;
                const panX = canZoom ? this.alignToDevicePixel(this.panX) : 0;
                const panY = canZoom ? this.alignToDevicePixel(this.panY) : 0;
                content.style.transform = zoom === MIN_ZOOM ? `` : `translate(${panX}px, ${panY}px) scale(${zoom})`;
                content.style.transformOrigin = `center center`;
                content.style.transition = this.isPanning || this.isPinching ? `none` : `transform 160ms ease`;
                content.style.cursor = canZoom && zoom > MIN_ZOOM ? (this.isPanning ? `grabbing` : `grab`) : ``;
                content.style.touchAction = canZoom ? `none` : ``;
                content.style.userSelect = canZoom && zoom > MIN_ZOOM ? `none` : ``;
                content.onmousedown = canZoom && zoom > MIN_ZOOM ? this.startPan : null;
                content.ontouchstart = canZoom ? this.startTouchGesture : null;
                content.ondragstart = canZoom && zoom > MIN_ZOOM ? event => event.preventDefault() : null;
            });
        },
        panPoint(event) {
            const source = event && event.touches && event.touches.length
                ? event.touches[0]
                : (event && event.changedTouches && event.changedTouches.length ? event.changedTouches[0] : event);
            if (!source) {
                return null;
            }
            return {
                x: Number(source.clientX !== undefined ? source.clientX : source.pageX),
                y: Number(source.clientY !== undefined ? source.clientY : source.pageY)
            };
        },
        touchDistance(event) {
            if (!event || !event.touches || event.touches.length < 2) {
                return 0;
            }
            const first = event.touches[0];
            const second = event.touches[1];
            const dx = Number(second.clientX) - Number(first.clientX);
            const dy = Number(second.clientY) - Number(first.clientY);
            return Math.sqrt(dx * dx + dy * dy);
        },
        startTouchGesture(event) {
            if (!this.canZoomCurrentFile) {
                return;
            }
            if (event && event.touches && event.touches.length > 1) {
                this.resetDoubleTap();
                this.resetSwipeClose();
                this.startPinch(event);
                return;
            }
            this.startImageTap(event);
            if (this.boundedZoom > MIN_ZOOM) {
                this.startPan(event);
                return;
            }
            this.startSwipeClose(event);
        },
        startImageTap(event) {
            if (!event || !event.touches || event.touches.length !== 1) return;
            const point = this.panPoint(event);
            if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
            this.tapCandidate = {point, time: Date.now(), content: event.currentTarget};
            this.addDocumentListener(`touchmove`, this.trackImageTap, true);
            this.addDocumentListener(`touchend`, this.finishImageTap, {capture: true, passive: false});
            this.addDocumentListener(`touchcancel`, this.resetDoubleTap, true);
        },
        trackImageTap(event) {
            const tap = this.tapCandidate;
            const point = this.panPoint(event);
            if (!tap || !point || event.touches.length !== 1
                || Math.hypot(point.x - tap.point.x, point.y - tap.point.y) > TAP_MOVE_TOLERANCE_PX) {
                this.resetDoubleTap();
            }
        },
        removeImageTapListeners() {
            this.removeDocumentListener(`touchmove`, this.trackImageTap, true);
            this.removeDocumentListener(`touchend`, this.finishImageTap, true);
            this.removeDocumentListener(`touchcancel`, this.resetDoubleTap, true);
        },
        resetDoubleTap() {
            this.tapCandidate = null;
            this.lastImageTap = null;
            this.removeImageTapListeners();
        },
        finishImageTap(event) {
            const tap = this.tapCandidate;
            const previous = this.lastImageTap;
            const point = this.panPoint(event);
            const now = Date.now();
            this.tapCandidate = null;
            this.removeImageTapListeners();
            if (!tap || !point || event.touches.length || now - tap.time > TAP_MAX_DURATION_MS
                || Math.hypot(point.x - tap.point.x, point.y - tap.point.y) > TAP_MOVE_TOLERANCE_PX) {
                this.lastImageTap = null;
                return;
            }
            if (!previous || previous.content !== tap.content || now - previous.time > DOUBLE_TAP_DELAY_MS
                || Math.hypot(point.x - previous.point.x, point.y - previous.point.y) > DOUBLE_TAP_DISTANCE_PX) {
                this.lastImageTap = {point, time: now, content: tap.content};
                return;
            }
            this.preventViewerEvent(event);
            const zoomed = this.boundedZoom > MIN_ZOOM;
            this.resetDoubleTap();
            this.resetSwipeClose();
            this.resetPinch();
            this.resetPan();
            if (!zoomed && tap.content) {
                const rect = tap.content.getBoundingClientRect();
                this.panX = (rect.left + rect.width / 2 - point.x) * (DOUBLE_TAP_ZOOM - 1);
                this.panY = (rect.top + rect.height / 2 - point.y) * (DOUBLE_TAP_ZOOM - 1);
            }
            this.setZoom(zoomed ? MIN_ZOOM : DOUBLE_TAP_ZOOM, {immediate: true});
        },
        startSwipeClose(event) {
            if (
                this.boundedZoom !== MIN_ZOOM
                || !this.canZoomCurrentFile
                || !event
                || !event.touches
                || event.touches.length !== 1
            ) {
                return;
            }
            const point = this.panPoint(event);
            if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
                return;
            }

            this.resetSwipeClose();
            this.isSwipeCloseTracking = true;
            this.swipeCloseStartX = point.x;
            this.swipeCloseStartY = point.y;
            this.swipeCloseContent = event.currentTarget || null;
            this.addSwipeCloseListeners();
        },
        handleSwipeCloseMove(event) {
            if (!this.isSwipeCloseTracking) {
                return;
            }
            if (
                this.boundedZoom !== MIN_ZOOM
                || !event
                || !event.touches
                || event.touches.length !== 1
            ) {
                this.resetSwipeClose();
                return;
            }

            const point = this.panPoint(event);
            if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
                return;
            }
            const deltaX = point.x - this.swipeCloseStartX;
            const deltaY = point.y - this.swipeCloseStartY;
            const absoluteX = Math.abs(deltaX);
            const absoluteY = Math.abs(deltaY);

            if (!this.isSwipeCloseActive) {
                if (Math.max(absoluteX, absoluteY) < SWIPE_CLOSE_DIRECTION_LOCK_PX) {
                    return;
                }
                const isUpwardSwipe = deltaY < 0;
                const isVerticalSwipe = absoluteY > absoluteX * SWIPE_CLOSE_VERTICAL_RATIO;
                if (!isUpwardSwipe || !isVerticalSwipe) {
                    this.resetSwipeClose();
                    return;
                }
                this.isSwipeCloseActive = true;
            }

            this.preventViewerEvent(event);
            this.swipeCloseOffsetY = Math.min(0, deltaY);
            this.applySwipeCloseOffset();
        },
        finishSwipeClose(event) {
            if (!this.isSwipeCloseTracking) {
                return;
            }
            const shouldClose = this.isSwipeCloseActive
                && this.swipeCloseOffsetY <= -this.getSwipeCloseThreshold();
            if (this.isSwipeCloseActive && event && event.preventDefault) {
                event.preventDefault();
            }
            this.resetSwipeClose();
            if (shouldClose) {
                // Даём Blueimp завершить свой touchend и вернуть слайд из промежуточной
                // позиции, после чего закрываем общий просмотрщик.
                if (typeof window !== `undefined`) {
                    window.setTimeout(() => {
                        if (this.isOpen && this.boundedZoom === MIN_ZOOM) {
                            this.close();
                        }
                    }, 0);
                } else {
                    this.close();
                }
            }
        },
        getSwipeCloseThreshold() {
            const viewportHeight = typeof window !== `undefined`
                ? Number(window.innerHeight) || 0
                : 0;
            const responsiveThreshold = viewportHeight * SWIPE_CLOSE_VIEWPORT_RATIO;
            return Math.min(
                SWIPE_CLOSE_MAX_DISTANCE_PX,
                Math.max(SWIPE_CLOSE_MIN_DISTANCE_PX, responsiveThreshold)
            );
        },
        applySwipeCloseOffset() {
            const content = this.swipeCloseContent;
            if (!content || !this.isSwipeCloseActive) {
                return;
            }
            const progress = Math.min(1, Math.abs(this.swipeCloseOffsetY) / this.getSwipeCloseThreshold());
            content.style.transition = `none`;
            content.style.transform = `translate3d(0, ${this.swipeCloseOffsetY}px, 0)`;
            content.style.opacity = String(1 - progress * 0.3);
        },
        resetSwipeClose() {
            const content = this.swipeCloseContent;
            this.removeSwipeCloseListeners();
            this.isSwipeCloseTracking = false;
            this.isSwipeCloseActive = false;
            this.swipeCloseStartX = 0;
            this.swipeCloseStartY = 0;
            this.swipeCloseOffsetY = 0;
            this.swipeCloseContent = null;

            if (content) {
                content.style.transition = `transform 160ms ease, opacity 160ms ease`;
                content.style.transform = ``;
                content.style.opacity = ``;
            }
        },
        addSwipeCloseListeners() {
            this.addDocumentListener(`touchmove`, this.handleSwipeCloseMove, {capture: true, passive: false});
            this.addDocumentListener(`touchend`, this.finishSwipeClose, true);
            this.addDocumentListener(`touchcancel`, this.resetSwipeClose, true);
        },
        removeSwipeCloseListeners() {
            this.removeDocumentListener(`touchmove`, this.handleSwipeCloseMove, true);
            this.removeDocumentListener(`touchend`, this.finishSwipeClose, true);
            this.removeDocumentListener(`touchcancel`, this.resetSwipeClose, true);
        },
        startPan(event) {
            if (this.boundedZoom <= MIN_ZOOM || !this.canZoomCurrentFile) {
                return;
            }
            if (event && event.type === `mousedown` && event.button !== 0) {
                return;
            }
            if (event && event.touches && event.touches.length > 1) {
                return;
            }
            const point = this.panPoint(event);
            if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
                return;
            }
            this.preventViewerEvent(event);
            this.isPanning = true;
            this.panStartX = point.x;
            this.panStartY = point.y;
            this.panOriginX = Number(this.panX) || 0;
            this.panOriginY = Number(this.panY) || 0;
            this.addPanListeners();
            this.applyZoom();
        },
        handlePanMove(event) {
            if (!this.isPanning) {
                return;
            }
            if (event && event.touches && event.touches.length > 1) {
                this.startPinch(event);
                return;
            }
            const point = this.panPoint(event);
            if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
                return;
            }
            this.preventViewerEvent(event);
            this.panX = this.panOriginX + point.x - this.panStartX;
            this.panY = this.panOriginY + point.y - this.panStartY;
            this.applyZoom();
        },
        stopPan(event) {
            if (!this.isPanning) {
                return;
            }
            this.preventViewerEvent(event);
            this.isPanning = false;
            this.removePanListeners();
            this.applyZoom();
        },
        startPinch(event) {
            this.resetDoubleTap();
            if (!this.canZoomCurrentFile) {
                return;
            }
            const distance = this.touchDistance(event);
            if (!Number.isFinite(distance) || distance <= 0) {
                return;
            }
            this.preventViewerEvent(event);
            this.removePanListeners();
            this.isPanning = false;
            this.isPinching = true;
            this.pinchStartDistance = distance;
            this.pinchStartZoom = this.boundedZoom;
            this.addPinchListeners();
            this.applyZoom();
        },
        handlePinchMove(event) {
            if (!this.isPinching) {
                return;
            }
            const distance = this.touchDistance(event);
            if (!Number.isFinite(distance) || distance <= 0 || !this.pinchStartDistance) {
                return;
            }
            this.preventViewerEvent(event);
            this.setZoom(this.pinchStartZoom * (distance / this.pinchStartDistance), {immediate: true});
        },
        stopPinch(event) {
            if (!this.isPinching) {
                return;
            }
            this.preventViewerEvent(event);
            this.isPinching = false;
            this.pinchStartDistance = 0;
            this.pinchStartZoom = this.boundedZoom;
            this.removePinchListeners();
            this.applyZoom();
        },
        addDocumentListener(type, handler, options) {
            if (typeof document === `undefined`) {
                return;
            }
            document.addEventListener(type, handler, options);
        },
        removeDocumentListener(type, handler, options) {
            if (typeof document === `undefined`) {
                return;
            }
            document.removeEventListener(type, handler, options);
        },
        addPanListeners() {
            this.addDocumentListener(`mousemove`, this.handlePanMove, true);
            this.addDocumentListener(`mouseup`, this.stopPan, true);
            this.addDocumentListener(`touchmove`, this.handlePanMove, {capture: true, passive: false});
            this.addDocumentListener(`touchend`, this.stopPan, true);
            this.addDocumentListener(`touchcancel`, this.stopPan, true);
        },
        removePanListeners() {
            this.removeDocumentListener(`mousemove`, this.handlePanMove, true);
            this.removeDocumentListener(`mouseup`, this.stopPan, true);
            this.removeDocumentListener(`touchmove`, this.handlePanMove, true);
            this.removeDocumentListener(`touchend`, this.stopPan, true);
            this.removeDocumentListener(`touchcancel`, this.stopPan, true);
        },
        addPinchListeners() {
            this.addDocumentListener(`touchmove`, this.handlePinchMove, {capture: true, passive: false});
            this.addDocumentListener(`touchend`, this.stopPinch, true);
            this.addDocumentListener(`touchcancel`, this.stopPinch, true);
        },
        removePinchListeners() {
            this.removeDocumentListener(`touchmove`, this.handlePinchMove, true);
            this.removeDocumentListener(`touchend`, this.stopPinch, true);
            this.removeDocumentListener(`touchcancel`, this.stopPinch, true);
        },
        addWheelListener() {
            this.addDocumentListener(`wheel`, this.handleWheel, {capture: true, passive: false});
        },
        removeWheelListener() {
            this.removeDocumentListener(`wheel`, this.handleWheel, true);
        },
        async runPrimaryAction() {
            if (!this.activePrimaryAction || this.primaryActionDisabled || this.primaryActionInProgress) {
                return;
            }
            this.primaryActionInProgress = true;
            try {
                if (typeof this.activePrimaryAction.handler === `function`) {
                    await this.activePrimaryAction.handler(this.currentFile, this);
                }
            } finally {
                this.primaryActionInProgress = false;
            }
        }
    }
};
</script>

<style scoped>
.app-file-viewer-annulled {
    background: rgba(127, 16, 27, .96);
    border: 2px solid #ff6b79;
    border-radius: 7px;
    color: #fff;
    left: 50%;
    max-width: calc(100vw - 32px);
    padding: 8px 13px;
    pointer-events: none;
    position: fixed;
    text-align: center;
    top: 18px;
    transform: translateX(-50%);
    width: max-content;
    z-index: 100002;
}

.app-file-viewer-annulled strong,
.app-file-viewer-annulled span {
    display: block;
}

.app-file-viewer-annulled strong {
    font-size: 15px;
    font-weight: 900;
}

.app-file-viewer-annulled span {
    font-size: 12px;
    margin-top: 3px;
}

.app-file-viewer-toolbar {
    position: fixed;
    right: 24px;
    bottom: 112px;
    z-index: 100000;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    border-radius: 8px;
    background: rgba(10, 16, 24, .78);
    box-shadow: 0 8px 24px rgba(0, 0, 0, .35);
}

.app-file-viewer-video-panel {
    align-items: center;
    display: flex;
    inset: 72px 24px 96px;
    justify-content: center;
    pointer-events: auto;
    position: fixed;
    z-index: 100000;
}

.app-file-viewer-video-panel video {
    background: #000;
    border-radius: 8px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, .45);
    max-height: 100%;
    max-width: 100%;
}

.app-file-viewer-tool {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, .34);
    border-radius: 7px;
    background: rgba(255, 255, 255, .12);
    color: #fff;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
}

.app-file-viewer-tool:hover,
.app-file-viewer-tool:focus {
    color: #fff;
    text-decoration: none;
    border-color: #fff;
    background: rgba(255, 255, 255, .2);
}

.app-file-viewer-tool:disabled {
    opacity: .45;
    cursor: default;
}

.app-file-viewer-tool--wide {
    width: 64px;
    font-weight: 700;
    font-size: 13px;
}

.app-file-viewer-file-actions {
    position: fixed;
    left: 50%;
    top: 50%;
    z-index: 100001;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 520px;
    max-width: calc(100vw - 36px);
    transform: translate(-50%, 64px);
}

.app-file-viewer-open-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 10px 16px;
    border: 1px solid rgba(255, 255, 255, .3);
    border-radius: 8px;
    background: rgba(33, 37, 41, .9);
    color: #fff;
    font-weight: 700;
    box-shadow: 0 10px 26px rgba(0, 0, 0, .32);
}

.app-file-viewer-open-cta:hover,
.app-file-viewer-open-cta:focus {
    color: #fff;
    text-decoration: none;
    background: rgba(52, 58, 64, .96);
}

.app-file-viewer-download-cta {
    display: flex;
    align-items: center;
    gap: 18px;
    width: 100%;
    padding: 18px 22px;
    border: 1px solid rgba(255, 255, 255, .28);
    border-radius: 8px;
    background: rgba(13, 110, 253, .94);
    color: #fff;
    box-shadow: 0 14px 34px rgba(0, 0, 0, .38);
}

.app-file-viewer-download-cta:hover,
.app-file-viewer-download-cta:focus {
    color: #fff;
    text-decoration: none;
    background: rgba(11, 94, 215, .98);
}

.app-file-viewer-download-cta__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    min-width: 52px;
    border-radius: 8px;
    background: rgba(255, 255, 255, .18);
    font-size: 26px;
}

.app-file-viewer-download-cta__text {
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}

.app-file-viewer-download-cta__text strong {
    font-size: 18px;
}

.app-file-viewer-download-cta__text small {
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgba(255, 255, 255, .86);
}

.app-file-viewer-notice {
    align-items: center;
    display: flex;
    inset: 82px 88px 128px;
    justify-content: center;
    pointer-events: none;
    position: fixed;
    z-index: 100001;
}

.app-file-viewer-notice-card {
    background: rgba(7, 17, 31, .94);
    border: 2px solid #ffc107;
    border-radius: 8px;
    box-shadow: 0 18px 54px rgba(0, 0, 0, .5);
    color: #fff;
    max-height: 100%;
    max-width: 680px;
    overflow-y: auto;
    padding: 24px 28px;
    text-align: center;
    width: 100%;
}

.app-file-viewer-notice-card strong {
    display: block;
    font-size: 28px;
    line-height: 1.2;
}

.app-file-viewer-notice-card p {
    color: #d7e3f4;
    font-size: 17px;
    line-height: 1.45;
    margin: 14px auto 0;
    max-width: 600px;
}

.app-file-viewer-notice-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin-top: 20px;
}

.app-file-viewer-notice-actions span {
    align-items: center;
    background: rgba(255, 255, 255, .1);
    border: 1px solid rgba(255, 255, 255, .25);
    border-radius: 7px;
    display: inline-flex;
    flex: 1 1 240px;
    font-size: 15px;
    font-weight: 700;
    gap: 8px;
    justify-content: center;
    min-height: 42px;
    padding: 9px 12px;
}

.app-file-viewer-primary-action {
    position: fixed;
    left: 50%;
    bottom: 112px;
    z-index: 100000;
    min-height: 40px;
    padding: 8px 14px;
    border: 1px solid rgba(255, 193, 7, .95);
    border-radius: 8px;
    background: rgba(255, 193, 7, .96);
    color: #212529;
    font-weight: 700;
    box-shadow: 0 8px 24px rgba(0, 0, 0, .28);
    transform: translateX(-50%);
}

.app-file-viewer-primary-action:disabled {
    opacity: .65;
}

>>> .blueimp-gallery {
    background: rgba(0, 0, 0, .94);
    z-index: 99990;
}

>>> .blueimp-gallery > .slides {
    top: 18px;
    right: 0;
    bottom: 104px;
    left: 0;
    height: calc(100% - 122px);
}

>>> .blueimp-gallery > .slides > .slide > .slide-content {
    max-width: 100%;
    max-height: 100%;
}

>>> .blueimp-gallery > .slides > .slide > img.slide-content {
    height: 100%;
    object-fit: contain;
    width: 100%;
}

>>> .blueimp-gallery > .title {
    left: 18px;
    right: 72px;
    top: 14px;
    padding: 6px 10px;
    border-radius: 6px;
    background: rgba(0, 0, 0, .48);
    color: #fff;
    font-size: 15px;
    line-height: 1.3;
    opacity: 1;
}

>>> .blueimp-gallery > .indicator {
    left: 18px;
    right: 18px;
    bottom: 18px;
    display: flex;
    justify-content: flex-start;
    gap: 7px;
    max-height: 70px;
    margin: 0;
    overflow-x: auto;
    overflow-y: hidden;
}

>>> .blueimp-gallery > .indicator > li {
    width: 58px;
    height: 44px;
    min-width: 58px;
    margin: 0;
    border-radius: 6px;
    border: 2px solid rgba(255, 255, 255, .32);
    background-size: cover;
    background-position: center;
    opacity: .72;
}

>>> .blueimp-gallery > .indicator > li:hover,
>>> .blueimp-gallery > .indicator > .active {
    border-color: #ffc107;
    opacity: 1;
}

>>> .blueimp-gallery > .indicator > li:after {
    display: none;
}

@media (max-width: 767.98px) {
    .app-file-viewer-video-panel {
        inset: 64px 8px 88px;
    }

    .app-file-viewer-video-panel video {
        border-radius: 6px;
        width: 100%;
    }

    .app-file-viewer-toolbar {
        right: 10px;
        bottom: 92px;
        gap: 6px;
        padding: 5px;
    }

    .app-file-viewer-tool {
        width: 36px;
        height: 36px;
    }

    .app-file-viewer-tool--wide {
        width: 56px;
        font-size: 12px;
    }

    .app-file-viewer-primary-action {
        left: 10px;
        right: 10px;
        bottom: 142px;
        transform: none;
    }

    .app-file-viewer-file-actions {
        left: 10px;
        right: 10px;
        top: auto;
        bottom: 148px;
        width: auto;
        max-width: none;
        transform: none;
    }

    .app-file-viewer-open-cta {
        min-height: 42px;
        padding: 9px 14px;
    }

    .app-file-viewer-download-cta {
        padding: 14px;
    }

    .app-file-viewer-download-cta__icon {
        width: 46px;
        height: 46px;
        min-width: 46px;
        font-size: 22px;
    }

    .app-file-viewer-download-cta__text strong {
        font-size: 16px;
    }

    .app-file-viewer-notice {
        inset: 66px 12px 96px;
    }

    .app-file-viewer-notice-card {
        padding: 18px 14px;
    }

    .app-file-viewer-notice-card strong {
        font-size: 20px;
    }

    .app-file-viewer-notice-card p {
        font-size: 14px;
        line-height: 1.4;
        margin-top: 10px;
    }

    .app-file-viewer-notice-actions {
        flex-direction: column;
        gap: 8px;
        margin-top: 14px;
    }

    .app-file-viewer-notice-actions span {
        flex-basis: auto;
        font-size: 13px;
        min-height: 40px;
        width: 100%;
    }

    >>> .blueimp-gallery > .slides {
        top: 10px;
        right: 0;
        bottom: 92px;
        left: 0;
        height: calc(100% - 102px);
    }

    >>> .blueimp-gallery > .title {
        top: 10px;
        left: 10px;
        right: 56px;
        max-height: 48px;
        overflow: hidden;
        font-size: 13px;
    }

    >>> .blueimp-gallery > .close {
        top: 8px;
        right: 10px;
    }

    >>> .blueimp-gallery > .indicator {
        left: 10px;
        right: 10px;
        bottom: 12px;
        justify-content: flex-start;
    }

    >>> .blueimp-gallery > .indicator > li {
        width: 52px;
        height: 40px;
        min-width: 52px;
    }
}
</style>
